import { Controller } from "@hotwired/stimulus"

// Ajax-form controller: submits a form asynchronously and renders the JSON
// response into `.form-results-success` / `.form-results-errors` alerts that
// live inside the form. Stays on the same page, no reload, no redirect.
//
// The server MUST respond with JSON of the shape:
//   { success: Boolean, message: String }
//
// Usage:
//   = form_for ..., html: {
//       data: {
//         controller: "ajax-form",
//         turbo: false,
//         action: "submit->ajax-form#submit",
//       },
//     } do |f|
//     = render partial: "/form_results_success", locals: { id: "my-form-success" }
//     = render partial: "/form_results_errors",  locals: { id: "my-form-errors" }
//     ... fields ...
//     = f.submit "Save"
//
// `data-turbo="false"` is important: it stops Turbo from intercepting the
// submit and trying to navigate based on the JSON response.
//
// Submit buttons inside the form get disabled while the request is in-flight
// to prevent double-submission.
//
// Composability: dispatches `ajax-form:success` and `ajax-form:error`
// CustomEvents (bubbling, cancelable) with detail `{ payload, response }`
// before rendering the inline alert. Calling `event.preventDefault()` in a
// listener suppresses the inline alert — useful for callers that want to
// redirect on success (e.g. `users--new-form`) or take over error handling.
export default class extends Controller {
  static values = {
    scrollIntoView: { type: Boolean, default: true },
  }

  async submit(event) {
    event.preventDefault()

    const form = this.element
    if (this.submitting) return
    this.submitting = true

    const submitButtons = form.querySelectorAll(
      "input[type=submit], button[type=submit], button:not([type])"
    )
    submitButtons.forEach((b) => { b.disabled = true })

    this.#hideAlerts()

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    const method = (form.getAttribute("method") || "post").toUpperCase()

    let response, payload
    try {
      response = await fetch(form.action, {
        method,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrf || "",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: new FormData(form),
        credentials: "same-origin",
      })
      try { payload = await response.json() } catch (_) { payload = null }
    } catch (err) {
      this.#showError("Network error. Please try again.")
      return this.#finish(submitButtons)
    }

    if (response.ok && payload && payload.success) {
      const evt = this.dispatch("success", {
        detail: { payload, response },
        cancelable: true,
      })
      if (!evt.defaultPrevented) {
        this.#showSuccess(payload.message || "Saved")
      }
    } else {
      const evt = this.dispatch("error", {
        detail: { payload, response },
        cancelable: true,
      })
      if (!evt.defaultPrevented) {
        const msg = (payload && payload.message) || "Something went wrong."
        this.#showError(msg)
      }
    }

    this.#finish(submitButtons)
  }

  #finish(submitButtons) {
    submitButtons.forEach((b) => { b.disabled = false })
    this.submitting = false
  }

  #hideAlerts() {
    this.element
      .querySelectorAll(".form-results-success, .form-results-errors")
      .forEach((el) => { el.style.display = "none" })
  }

  #showSuccess(message) { this.#showAlert(".form-results-success", message) }
  #showError(message)   { this.#showAlert(".form-results-errors",  message) }

  #showAlert(selector, message) {
    const el = this.element.querySelector(selector)
    if (!el) {
      console.warn(`[ajax-form] no ${selector} inside form`, this.element)
      return
    }
    const content = el.querySelector(".content") || el
    content.innerHTML = message
    el.classList.remove("tmp-hide")
    el.style.display = ""
    if (this.scrollIntoViewValue) {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }
}
